import { vec3 } from 'gl-matrix';

export type VolumeHeightSample = {
  height: number;
  normal: vec3;
};

/**
 * Tagged floor height. `terrainOrigin` is true when the returned `y` is the floor
 * of the open span sitting directly above the lowest solid in a column whose
 * lowest solid is terrain-only — i.e. the player-walkable ground in a mixed tile.
 * The tiled layer uses this to substitute the smooth heightmap Y for `y`.
 */
export type FloorTagged = { y: number; terrainOrigin: boolean };

/** Common interface for single VolumeGrid and TiledVolumeGrid. */
export interface VolumeGridLike {
  voxelSize: number;
  isSolidAt(x: number, y: number, z: number): boolean;
  getFloorHeight(x: number, y: number, z: number): number | null;
  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null;
  getCeilingHeight(x: number, y: number, z: number): number | null;
  getCeilingHeightAbove(x: number, y: number, z: number): number | null;
  getFloorHeightBelow(x: number, y: number, z: number): number | null;
  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null;
  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null;
  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null;
  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean;
  /**
   * True when a real volgrid tile covers (x, z). Non-tiled grids cover the
   * whole world and always return true; the tiled grid only returns true for
   * loaded tiles that have a published volgrid.
   */
  hasTileAt(x: number, z: number): boolean;
  /**
   * Iterate every leaf node and invoke `callback` with its world-space rect,
   * node type, byte offset (for callers that need to read leaf data inline),
   * and quadtree depth. For tiled grids, only currently-loaded tiles are visited.
   */
  forEachLeaf(callback: VolumeLeafCallback): void;
}

export type VolumeLeafCallback = (
  worldX: number,
  worldZ: number,
  worldW: number,
  worldD: number,
  nodeType: number,
  nodeOffset: number,
  depth: number,
) => void;

const MAGIC = 0x4e564f4c; // "NVOL"
const VERSION = 3;

// Node type markers matching backend
const NODE_BRANCH = 0;
const NODE_FLAT = 1;
const NODE_COMPLEX = 2;
const NODE_EMPTY = 3;

const UNBOUNDED_CEILING = 90000;
const HEADER_SIZE = 32;

/**
 * v3 per-column header byte layout: high bit = `lowestSolidIsTerrain`,
 * low 7 bits = span count. Values are written together by `QuadTreeGridWriter`
 * and the writer asserts spanCount ≤ 127 so the bit and count never alias.
 */
const COLUMN_HEADER_TERRAIN_BIT = 0x80;
const COLUMN_HEADER_SPAN_MASK = 0x7f;

/**
 * Single-threaded reusable scratch object passed to scan helpers as their `outTag`
 * parameter. Reused across queries to avoid one allocation per floor query — JS
 * is single-threaded so there's no concurrent caller to worry about.
 */
const SCRATCH_TAG = { terrainOrigin: false };

/**
 * ArrayBuffer-backed volume grid. Instead of deserializing into JS objects,
 * the raw binary data is kept and navigated directly via DataView during queries.
 * A lightweight branch-offset index (Map of byte offsets) is built in a single scan pass.
 * Memory usage is ~1.05x file size instead of ~2x.
 */
export class VolumeGrid implements VolumeGridLike {
  public voxelSize: number;
  private minX: number;
  private minZ: number;
  private width: number;
  private depth: number;
  private view: DataView;
  /** For each branch node byte offset, stores [child0, child1, child2, child3] byte offsets. */
  private branchChildren: Map<number, Uint32Array>;
  private rootOffset: number;

  private constructor(
    view: DataView,
    minX: number,
    minZ: number,
    voxelSize: number,
    width: number,
    depth: number,
    branchChildren: Map<number, Uint32Array>,
    rootOffset: number,
  ) {
    this.view = view;
    this.minX = minX;
    this.minZ = minZ;
    this.voxelSize = voxelSize;
    this.width = width;
    this.depth = depth;
    this.branchChildren = branchChildren;
    this.rootOffset = rootOffset;
  }

  /**
   * Reconstruct a VolumeGrid from a transferred buffer + scan result produced
   * by the tile-parse Web Worker. The buffer arrives back on the main thread
   * via zero-copy transfer; `branchFlat` is decoded into the branch-children
   * Map in a single O(numBranches) pass.
   */
  static fromScan(buffer: ArrayBuffer, result: VolgridScanResult): VolumeGrid {
    const { header, branchFlat } = result;
    const branchChildren = new Map<number, Uint32Array>();
    for (let i = 0; i < branchFlat.length; i += 5) {
      branchChildren.set(
        branchFlat[i],
        new Uint32Array([branchFlat[i + 1], branchFlat[i + 2], branchFlat[i + 3], branchFlat[i + 4]]),
      );
    }
    return new VolumeGrid(
      new DataView(buffer),
      header.minX,
      header.minZ,
      header.voxelSize,
      header.width,
      header.depth,
      branchChildren,
      HEADER_SIZE,
    );
  }

  static read(buffer: ArrayBuffer): VolumeGrid {
    const view = new DataView(buffer);

    const magic = view.getUint32(0, true);
    if (magic !== MAGIC) {
      throw new Error(`Invalid volgrid file (bad magic: 0x${magic.toString(16)})`);
    }
    const version = view.getUint32(4, true);
    if (version !== VERSION) {
      throw new Error(`Unsupported volgrid version: ${version}`);
    }

    const minX = view.getFloat32(8, true);
    const minZ = view.getFloat32(12, true);
    const voxelSize = view.getFloat32(16, true);
    const width = view.getUint32(20, true);
    const depth = view.getUint32(24, true);
    // offset 28: cellCount (unused in v2)

    // Single pass: scan the tree to build branch child offset index
    const branchChildren = new Map<number, Uint32Array>();
    scanNode(view, HEADER_SIZE, branchChildren);

    return new VolumeGrid(view, minX, minZ, voxelSize, width, depth, branchChildren, HEADER_SIZE);
  }

  // --- Query methods ---

  hasTileAt(_x: number, _z: number): boolean {
    return true;
  }

  isSolidAt(x: number, y: number, z: number): boolean {
    const gx = (x - this.minX) / this.voxelSize;
    const gz = (z - this.minZ) / this.voxelSize;
    if (gx < 0 || gx >= this.width || gz < 0 || gz >= this.depth) return false;

    const leaf = this.findLeaf(gx, gz);
    if (!leaf) return false;

    if (leaf.type === NODE_FLAT) {
      const floor = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 0);
      const ceiling = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 8);
      return y < floor || (ceiling < UNBOUNDED_CEILING && y > ceiling);
    }

    if (leaf.type === NODE_COMPLEX) {
      return this.scanComplexSolid(leaf.offset, leaf.tx, leaf.tz, y);
    }

    return false;
  }

  getFloorHeight(x: number, y: number, z: number): number | null {
    return this.getFloorHeightTagged(x, y, z)?.y ?? null;
  }

  /**
   * Floor query with the per-column terrain-origin tag. Use this from the tiled
   * layer to decide whether to substitute the smooth heightmap Y for the
   * returned `y` (set when this is the lowest open span in a tagged column).
   */
  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null {
    const gx = (x - this.minX) / this.voxelSize;
    const gz = (z - this.minZ) / this.voxelSize;
    if (gx < 0 || gx >= this.width || gz < 0 || gz >= this.depth) return null;

    const leaf = this.findLeaf(gx, gz);
    if (!leaf) return null;

    if (leaf.type === NODE_FLAT) {
      const floor = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 0);
      const ceiling = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 8);
      if (y >= floor && (ceiling >= UNBOUNDED_CEILING || y <= ceiling)) {
        return { y: floor, terrainOrigin: false };
      }
      return null;
    }

    if (leaf.type === NODE_COMPLEX) {
      const tag = SCRATCH_TAG;
      tag.terrainOrigin = false;
      const yResult = this.scanComplexOpen(leaf.offset, leaf.tx, leaf.tz, y, 'floor', tag);
      if (yResult === null) return null;
      return { y: yResult, terrainOrigin: tag.terrainOrigin };
    }

    return null;
  }

  getCeilingHeight(x: number, y: number, z: number): number | null {
    const gx = (x - this.minX) / this.voxelSize;
    const gz = (z - this.minZ) / this.voxelSize;
    if (gx < 0 || gx >= this.width || gz < 0 || gz >= this.depth) return null;

    const leaf = this.findLeaf(gx, gz);
    if (!leaf) return null;

    if (leaf.type === NODE_FLAT) {
      const floor = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 0);
      const ceiling = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 8);
      if (y >= floor && (ceiling >= UNBOUNDED_CEILING || y <= ceiling)) {
        return ceiling;
      }
      return null;
    }

    if (leaf.type === NODE_COMPLEX) {
      return this.scanComplexOpen(leaf.offset, leaf.tx, leaf.tz, y, 'ceiling');
    }

    return null;
  }

  getCeilingHeightAbove(x: number, y: number, z: number): number | null {
    const gx = (x - this.minX) / this.voxelSize;
    const gz = (z - this.minZ) / this.voxelSize;
    if (gx < 0 || gx >= this.width || gz < 0 || gz >= this.depth) return null;

    const leaf = this.findLeaf(gx, gz);
    if (!leaf) return null;

    if (leaf.type === NODE_FLAT) {
      return this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 8);
    }

    if (leaf.type === NODE_COMPLEX) {
      return this.scanComplexBest(leaf.offset, leaf.tx, leaf.tz, y, 'ceilingAbove');
    }

    return null;
  }

  getFloorHeightBelow(x: number, y: number, z: number): number | null {
    return this.getFloorHeightBelowTagged(x, y, z)?.y ?? null;
  }

  /** Best floor at-or-below `y`, with the per-column terrain-origin tag. */
  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null {
    const gx = (x - this.minX) / this.voxelSize;
    const gz = (z - this.minZ) / this.voxelSize;
    if (gx < 0 || gx >= this.width || gz < 0 || gz >= this.depth) return null;

    const leaf = this.findLeaf(gx, gz);
    if (!leaf) return null;

    if (leaf.type === NODE_FLAT) {
      const floor = this.readFlatInterpolated(leaf.offset, leaf.tx, leaf.tz, 0);
      return { y: floor, terrainOrigin: false };
    }

    if (leaf.type === NODE_COMPLEX) {
      const tag = SCRATCH_TAG;
      tag.terrainOrigin = false;
      const yResult = this.scanComplexBest(leaf.offset, leaf.tx, leaf.tz, y, 'floorBelow', tag);
      if (yResult === null) return null;
      return { y: yResult, terrainOrigin: tag.terrainOrigin };
    }

    return null;
  }

  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null {
    const fx = (x - this.minX) / this.voxelSize - 0.5;
    const fz = (z - this.minZ) / this.voxelSize - 0.5;
    const xi = Math.floor(fx);
    const zi = Math.floor(fz);
    const tx = fx - xi;
    const tz = fz - zi;

    const h00 = this.floorHeightAtGrid(xi, zi, y);
    const h10 = this.floorHeightAtGrid(xi + 1, zi, y);
    const h01 = this.floorHeightAtGrid(xi, zi + 1, y);
    const h11 = this.floorHeightAtGrid(xi + 1, zi + 1, y);

    const valid = [h00, h10, h01, h11].filter((h) => h !== null) as number[];
    if (valid.length === 0) return null;

    const fallback = valid.reduce((a, b) => a + b, 0) / valid.length;
    const s00 = h00 ?? fallback;
    const s10 = h10 ?? fallback;
    const s01 = h01 ?? fallback;
    const s11 = h11 ?? fallback;

    const top = s00 + (s10 - s00) * tx;
    const bottom = s01 + (s11 - s01) * tx;
    return top + (bottom - top) * tz;
  }

  private floorHeightAtGrid(xi: number, zi: number, y: number): number | null {
    if (xi < 0 || xi >= this.width || zi < 0 || zi >= this.depth) return null;
    const worldX = this.minX + (xi + 0.5) * this.voxelSize;
    const worldZ = this.minZ + (zi + 0.5) * this.voxelSize;
    return this.getFloorHeightBelow(worldX, y, worldZ);
  }

  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null {
    let h = this.getFloorHeight(x, y, z);
    if (h === null) return null;

    let e = this.voxelSize;
    let hx = this.getFloorHeight(x + e, y, z) ?? h;
    let hz = this.getFloorHeight(x, y, z + e) ?? h;

    let dhdx = (hx - h) / e;
    let dhdz = (hz - h) / e;
    let normal = vec3.normalize(vec3.create(), vec3.fromValues(-dhdx, 1, -dhdz));

    return { height: h, normal };
  }

  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
    let dx = x1 - x0;
    let dy = y1 - y0;
    let dz = z1 - z0;
    let dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < 0.001) return this.isSolidAt(x1, y1, z1);

    let steps = Math.ceil(dist / this.voxelSize);
    for (let i = 0; i <= steps; i++) {
      let t = i / steps;
      if (this.isSolidAt(x0 + dx * t, y0 + dy * t, z0 + dz * t)) {
        return true;
      }
    }
    return false;
  }

  // --- Tree traversal (operates on byte offsets) ---

  private findLeaf(
    gx: number,
    gz: number,
  ): { type: number; offset: number; tx: number; tz: number } | null {
    return this.descendOffset(this.rootOffset, 0, 0, this.width, this.depth, gx, gz);
  }

  private descendOffset(
    nodeOffset: number,
    x0: number,
    z0: number,
    w: number,
    h: number,
    gx: number,
    gz: number,
  ): { type: number; offset: number; tx: number; tz: number } | null {
    const nodeType = this.view.getUint8(nodeOffset);

    if (nodeType === NODE_EMPTY) return null;

    if (nodeType === NODE_FLAT) {
      const tx = w > 0 ? (gx - x0) / w : 0;
      const tz = h > 0 ? (gz - z0) / h : 0;
      return { type: NODE_FLAT, offset: nodeOffset + 1, tx, tz };
    }

    if (nodeType === NODE_COMPLEX) {
      const tx = gx - x0;
      const tz = gz - z0;
      return { type: NODE_COMPLEX, offset: nodeOffset + 1, tx, tz };
    }

    // Branch — descend to correct child
    const halfW = Math.floor(w / 2);
    const halfH = Math.floor(h / 2);
    const rightW = w - halfW;
    const bottomH = h - halfH;

    const children = this.branchChildren.get(nodeOffset)!;
    const inRight = gx >= x0 + halfW;
    const inBottom = gz >= z0 + halfH;

    if (!inRight && !inBottom) {
      return this.descendOffset(children[0], x0, z0, halfW, halfH, gx, gz);
    } else if (inRight && !inBottom) {
      return this.descendOffset(children[1], x0 + halfW, z0, rightW, halfH, gx, gz);
    } else if (!inRight && inBottom) {
      return this.descendOffset(children[2], x0, z0 + halfH, halfW, bottomH, gx, gz);
    } else {
      return this.descendOffset(children[3], x0 + halfW, z0 + halfH, rightW, bottomH, gx, gz);
    }
  }

  // --- Flat leaf: read int16 corners directly from buffer ---

  /**
   * Read bilinear-interpolated value from a flat leaf's corner data.
   * @param dataOffset byte offset of the first corner int16 (after type byte)
   * @param cornerByteOffset 0 for floor corners, 8 for ceiling corners
   */
  private readFlatInterpolated(
    dataOffset: number,
    tx: number,
    tz: number,
    cornerByteOffset: number,
  ): number {
    const o = dataOffset + cornerByteOffset;
    const vs = this.voxelSize;
    const nw = this.view.getInt16(o, true) * vs;
    const ne = this.view.getInt16(o + 2, true) * vs;
    const sw = this.view.getInt16(o + 4, true) * vs;
    const se = this.view.getInt16(o + 6, true) * vs;

    const top = nw + (ne - nw) * tx;
    const bottom = sw + (se - sw) * tx;
    return top + (bottom - top) * tz;
  }

  // --- Complex leaf: scan column data in-place from buffer ---

  /**
   * Find the column at (lx, lz) in a complex leaf and scan its spans.
   * dataOffset points to the first byte after the node type (localWidth int16).
   *
   * Returns null when the column isn't in this leaf — v3 omits pure-terrain columns
   * entirely, so callers should treat a missing column as "delegate to terrain
   * fallback" (the tiled layer applies the actual heightmap substitution).
   */
  private findColumnOffset(
    dataOffset: number,
    localX: number,
    localZ: number,
  ): { spanOffset: number; localWidth: number; lowestSolidIsTerrain: boolean } | null {
    const lx = Math.floor(localX);
    const lz = Math.floor(localZ);
    const localWidth = this.view.getInt16(dataOffset, true);
    const localDepth = this.view.getInt16(dataOffset + 2, true);
    if (lx < 0 || lx >= localWidth || lz < 0 || lz >= localDepth) return null;

    const columnCount = this.view.getInt16(dataOffset + 4, true);
    const targetKey = lz * localWidth + lx;

    let off = dataOffset + 6;
    for (let c = 0; c < columnCount; c++) {
      const clx = this.view.getInt16(off, true);
      const clz = this.view.getInt16(off + 2, true);
      const key = clz * localWidth + clx;

      const header = this.view.getUint8(off + 4);
      const spanCount = header & COLUMN_HEADER_SPAN_MASK;
      const lowestSolidIsTerrain = (header & COLUMN_HEADER_TERRAIN_BIT) !== 0;
      const typeByteCount = Math.ceil(spanCount / 8);
      const columnDataSize = 4 + 1 + typeByteCount + spanCount * 2; // lx,lz + header + typeBits + heights

      if (key === targetKey) {
        return { spanOffset: off + 4, localWidth, lowestSolidIsTerrain };
      }

      off += columnDataSize;
    }

    return null;
  }

  /** Check if y is inside any solid range at the given complex leaf column. */
  private scanComplexSolid(dataOffset: number, localX: number, localZ: number, y: number): boolean {
    const col = this.findColumnOffset(dataOffset, localX, localZ);
    if (!col) return false;

    const vs = this.voxelSize;
    let off = col.spanOffset;
    const spanCount = this.view.getUint8(off) & COLUMN_HEADER_SPAN_MASK;
    off += 1;

    const typeByteCount = Math.ceil(spanCount / 8);
    const typeBitsOffset = off;
    off += typeByteCount;
    const heightsOffset = off;

    for (let s = 0; s < spanCount; s++) {
      const byteIdx = s >> 3;
      const bitIdx = s & 7;
      const isSolid = (this.view.getUint8(typeBitsOffset + byteIdx) & (1 << bitIdx)) !== 0;
      if (!isSolid) continue;

      const bottom = this.view.getInt16(heightsOffset + s * 2, true) * vs;
      const top =
        s + 1 < spanCount
          ? this.view.getInt16(heightsOffset + (s + 1) * 2, true) * vs
          : UNBOUNDED_CEILING;
      if (y >= bottom && y <= top) return true;
    }

    return false;
  }

  /**
   * Find open range containing y and return floor or ceiling. When `outTag` is
   * provided, sets `outTag.terrainOrigin = true` iff the column is tagged AND
   * the returned value is the bottom of the open span sitting directly above
   * the lowest solid (`floor` queries only — ceilings can never be terrain).
   */
  private scanComplexOpen(
    dataOffset: number,
    localX: number,
    localZ: number,
    y: number,
    which: 'floor' | 'ceiling',
    outTag?: { terrainOrigin: boolean },
  ): number | null {
    const col = this.findColumnOffset(dataOffset, localX, localZ);
    if (!col) return null;

    const vs = this.voxelSize;
    let off = col.spanOffset;
    const spanCount = this.view.getUint8(off) & COLUMN_HEADER_SPAN_MASK;
    off += 1;

    const typeByteCount = Math.ceil(spanCount / 8);
    const typeBitsOffset = off;
    off += typeByteCount;
    const heightsOffset = off;

    let firstOpenSeen = false;
    for (let s = 0; s < spanCount; s++) {
      const byteIdx = s >> 3;
      const bitIdx = s & 7;
      const isSolid = (this.view.getUint8(typeBitsOffset + byteIdx) & (1 << bitIdx)) !== 0;
      if (isSolid) continue;

      const bottom = this.view.getInt16(heightsOffset + s * 2, true) * vs;
      const top =
        s + 1 < spanCount
          ? this.view.getInt16(heightsOffset + (s + 1) * 2, true) * vs
          : UNBOUNDED_CEILING;

      const isFirstOpen = !firstOpenSeen;
      firstOpenSeen = true;

      if (y >= bottom && y <= top) {
        if (outTag && which === 'floor' && isFirstOpen && col.lowestSolidIsTerrain && s > 0) {
          // s > 0 means the lowest span is solid (terrain); this open span sits on top.
          outTag.terrainOrigin = true;
        }
        return which === 'floor' ? bottom : top;
      }
    }

    return null;
  }

  /**
   * Find best floor-below or ceiling-above across all open spans. When `outTag`
   * is provided, applies the same terrain-origin rule as [scanComplexOpen]: bit
   * is set iff the chosen value is the bottom of the open span directly above
   * the lowest solid.
   */
  private scanComplexBest(
    dataOffset: number,
    localX: number,
    localZ: number,
    y: number,
    which: 'floorBelow' | 'ceilingAbove',
    outTag?: { terrainOrigin: boolean },
  ): number | null {
    const col = this.findColumnOffset(dataOffset, localX, localZ);
    if (!col) return null;

    const vs = this.voxelSize;
    const tolerance = vs;
    let off = col.spanOffset;
    const spanCount = this.view.getUint8(off) & COLUMN_HEADER_SPAN_MASK;
    off += 1;

    const typeByteCount = Math.ceil(spanCount / 8);
    const typeBitsOffset = off;
    off += typeByteCount;
    const heightsOffset = off;

    let best: number | null = null;
    let bestIsFirstOpen = false;
    let firstOpenSeen = false;

    for (let s = 0; s < spanCount; s++) {
      const byteIdx = s >> 3;
      const bitIdx = s & 7;
      const isSolid = (this.view.getUint8(typeBitsOffset + byteIdx) & (1 << bitIdx)) !== 0;
      if (isSolid) continue;

      const bottom = this.view.getInt16(heightsOffset + s * 2, true) * vs;
      const top =
        s + 1 < spanCount
          ? this.view.getInt16(heightsOffset + (s + 1) * 2, true) * vs
          : UNBOUNDED_CEILING;

      const isFirstOpen = !firstOpenSeen;
      firstOpenSeen = true;

      if (which === 'floorBelow') {
        if (bottom <= y + tolerance && (best === null || bottom > best)) {
          best = bottom;
          // s > 0 guarantees the lowest span was solid (the terrain we'd substitute).
          bestIsFirstOpen = isFirstOpen && s > 0;
        }
      } else {
        if (top >= y - tolerance && (best === null || top < best)) {
          best = top;
        }
      }
    }

    if (outTag && which === 'floorBelow' && best !== null && bestIsFirstOpen && col.lowestSolidIsTerrain) {
      outTag.terrainOrigin = true;
    }
    return best;
  }

  // --- Debug visualization support ---

  forEachLeaf(callback: VolumeLeafCallback): void {
    this.walkLeaves(this.rootOffset, 0, 0, this.width, this.depth, 0, callback);
  }

  private walkLeaves(
    nodeOffset: number,
    x0: number,
    z0: number,
    w: number,
    h: number,
    depth: number,
    callback: VolumeLeafCallback,
  ): void {
    const nodeType = this.view.getUint8(nodeOffset);

    if (nodeType === NODE_BRANCH) {
      const halfW = Math.floor(w / 2);
      const halfH = Math.floor(h / 2);
      const rightW = w - halfW;
      const bottomH = h - halfH;
      const children = this.branchChildren.get(nodeOffset)!;
      this.walkLeaves(children[0], x0, z0, halfW, halfH, depth + 1, callback);
      this.walkLeaves(children[1], x0 + halfW, z0, rightW, halfH, depth + 1, callback);
      this.walkLeaves(children[2], x0, z0 + halfH, halfW, bottomH, depth + 1, callback);
      this.walkLeaves(children[3], x0 + halfW, z0 + halfH, rightW, bottomH, depth + 1, callback);
    } else {
      const worldX = this.minX + x0 * this.voxelSize;
      const worldZ = this.minZ + z0 * this.voxelSize;
      const worldW = w * this.voxelSize;
      const worldD = h * this.voxelSize;
      callback(worldX, worldZ, worldW, worldD, nodeType, nodeOffset, depth);
    }
  }
}

// --- Scan pass: build branch child offset index ---

/** Recursively scan the binary tree, recording branch child offsets. Returns next offset. */
function scanNode(
  view: DataView,
  offset: number,
  branchChildren: Map<number, Uint32Array>,
): number {
  const nodeType = view.getUint8(offset);
  const nodeStart = offset;
  offset += 1;

  switch (nodeType) {
    case NODE_BRANCH: {
      const children = new Uint32Array(4);
      for (let i = 0; i < 4; i++) {
        children[i] = offset;
        offset = scanNode(view, offset, branchChildren);
      }
      branchChildren.set(nodeStart, children);
      return offset;
    }

    case NODE_FLAT: {
      // 8 x int16 (4 floor corners + 4 ceiling corners)
      return offset + 16;
    }

    case NODE_COMPLEX: {
      const localWidth = view.getInt16(offset, true);
      offset += 2;
      offset += 2; // localDepth
      const columnCount = view.getInt16(offset, true);
      offset += 2;

      for (let c = 0; c < columnCount; c++) {
        offset += 4; // lx, lz
        const spanCount = view.getUint8(offset) & COLUMN_HEADER_SPAN_MASK;
        offset += 1;
        const typeByteCount = Math.ceil(spanCount / 8);
        offset += typeByteCount;
        offset += spanCount * 2; // heights
      }

      return offset;
    }

    case NODE_EMPTY: {
      return offset;
    }

    default:
      throw new Error(`Unknown quadtree node type: ${nodeType}`);
  }
}

// --- Worker-transfer support ---

/**
 * Result of the scan pass over a volgrid buffer. Fully transferable — both
 * the `branchFlat` typed array and the original `buffer` can be moved to the
 * main thread via `postMessage` with zero copy.
 *
 * Layout of `branchFlat`:
 *   [branchByteOffset, child0, child1, child2, child3, ...] × numBranchNodes
 */
export type VolgridScanResult = {
  header: { minX: number; minZ: number; voxelSize: number; width: number; depth: number };
  branchFlat: Uint32Array;
};

/**
 * Run only the scan pass on a volgrid buffer and return a transferable result.
 * Designed to run inside a Web Worker — produces the data needed to reconstruct
 * a VolumeGrid on the main thread via {@link VolumeGrid.fromScan}.
 */
export function scanVolgrid(buffer: ArrayBuffer): VolgridScanResult {
  const view = new DataView(buffer);
  const magic = view.getUint32(0, true);
  if (magic !== MAGIC) throw new Error(`Invalid volgrid (bad magic: 0x${magic.toString(16)})`);
  const version = view.getUint32(4, true);
  if (version !== VERSION) throw new Error(`Unsupported volgrid version: ${version}`);

  const minX = view.getFloat32(8, true);
  const minZ = view.getFloat32(12, true);
  const voxelSize = view.getFloat32(16, true);
  const width = view.getUint32(20, true);
  const depth = view.getUint32(24, true);

  const branchChildren = new Map<number, Uint32Array>();
  scanNode(view, HEADER_SIZE, branchChildren);

  const branchFlat = new Uint32Array(branchChildren.size * 5);
  let idx = 0;
  for (const [offset, children] of branchChildren) {
    branchFlat[idx++] = offset;
    branchFlat[idx++] = children[0];
    branchFlat[idx++] = children[1];
    branchFlat[idx++] = children[2];
    branchFlat[idx++] = children[3];
  }

  return { header: { minX, minZ, voxelSize, width, depth }, branchFlat };
}
